import { useState, type ReactNode } from 'react'
import { CircleHelp, Info } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui'
import { useI18n } from '@/i18n/I18nProvider'
import { cn } from '@/lib/utils'

/**
 * The two ways this application explains itself in place.
 *
 * `/uputstvo` answers "how does this screen work" for somebody who sits down
 * and reads. Neither of these is that. They answer the question somebody has
 * *while their hands are on the form*: what exactly goes in this field, and why
 * did that button just refuse me. A guide nobody has opened cannot answer that,
 * and the alternative — a paragraph of helper text under every input — makes
 * the form itself unreadable.
 *
 * ## Why two, and when to reach for which
 *
 * `Hint` attaches to one label: one sentence, on demand, costing 14px of space
 * whether or not anybody uses it. `HelpNote` sits above a screen or a section
 * and explains a rule that governs all of it — the kind of thing somebody needs
 * to know *before* they start typing, not after they have been refused.
 *
 * Neither is a place for text that the interface should simply have said
 * properly. If a field needs a tooltip to be comprehensible, the first thing to
 * try is a better label.
 */

/**
 * A question mark beside a label, with one sentence behind it.
 *
 * ## Why it is a button and not a hover target
 *
 * Half the people using this application are on a phone in a pogon. A tooltip
 * that opens on hover does not exist for them: there is no hover on a
 * touchscreen, and the usual fallback — "it opens on focus, and a tap focuses
 * it" — is exactly the kind of thing that works on the machine it was written
 * on. So the open state is held here and a press toggles it, which works the
 * same on a mouse, a finger and a keyboard. Hover still opens it on a desktop,
 * because base-ui drives the same state.
 *
 * ## Why the icon is small and grey
 *
 * It appears beside a great many labels, and on most of them nobody will ever
 * press it. At full contrast the page turns into a field of question marks and
 * the eye stops reading the labels themselves, which are the part that matters.
 */
export function Hint({
  children,
  className,
  side = 'top',
}: {
  /** One sentence. If it needs two, it probably wants a `HelpNote`. */
  children: ReactNode
  className?: string
  side?: 'top' | 'bottom' | 'left' | 'right'
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger
        render={
          <button
            type="button"
            // Not `aria-hidden`. The sentence inside is often the only place a
            // rule is written down, and a screen reader user needs it as much
            // as anybody — more, since they cannot see the disabled button that
            // the rule explains.
            aria-label={t('help.explain')}
            onClick={() => setOpen((it) => !it)}
            className={cn(
              'text-muted-foreground/70 hover:text-foreground focus-visible:ring-ring relative inline-flex shrink-0 cursor-help items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:outline-none',
              /*
               * A bigger target than the icon, and deliberately not 44px.
               *
               * `form.tsx` sets 44px as this application's minimum for anything
               * a finger has to hit, and gives the reason: the primary user is
               * in work gloves beside a machine. (With a mouse the controls
               * close up to 36px — the `control` token in index.css — but this
               * is about the finger.) A 14px icon is a seventh of
               * that. Padding cannot fix it — this sits inline beside a label,
               * so real padding would push the label off its baseline and space
               * out every heading the hint appears in. An absolutely positioned
               * overlay costs no layout, which makes it the obvious answer.
               *
               * The obvious answer is wrong at 44px. The overlay would need to
               * reach 15px in every direction, and a hint is not alone on its
               * row — it sits beside sort buttons in `WorkforceTable`, beside
               * filter controls, beside the labels of fields. It comes after
               * those in the DOM, so its overlay wins the hit test, and the
               * result is a question mark quietly eating taps meant for the
               * control next to it. Mis-hitting a hint opens a tooltip;
               * mis-hitting a sort button re-sorts a table somebody was reading.
               * The larger target is not worth the taps it steals.
               *
               * 8px, so about 30px square: more than double the area, and
               * smaller than the `gap-1.5`/`gap-2` these rows are built with.
               * A genuine 44px needs the hint to own layout space — a
               * `labelHint` slot on `Field` — which is a change to `form.tsx`,
               * not to this file.
               */
              "after:absolute after:-inset-2 after:content-['']",
              className,
            )}
          />
        }
      >
        {/*
          `size-3.5` rather than the inherited `size-4`: this sits on the
          baseline of a label, and at 16px it reads as another control rather
          than as a footnote to one.
        */}
        <CircleHelp className="size-3.5" aria-hidden />
      </TooltipTrigger>

      <TooltipContent side={side} className="max-w-[16rem] leading-relaxed">
        {children}
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * A rule that governs a whole screen, stated before anybody starts.
 *
 * Used for the things this application refuses to do and the reasons are not
 * guessable from the form: a 5S template locks once it has been used, a work
 * centre cannot exist before a template for its type does, a submitted
 * evaluation never changes. Somebody who reads this first does the right thing;
 * somebody who does not gets the same sentence back as an error, which is worse
 * only in that it wasted their time.
 *
 * `to` links into the printed guide at the section that covers this screen, so
 * the short form on the screen and the long form in `/uputstvo` are one gesture
 * apart rather than two documents.
 */
export function HelpNote({
  children,
  to,
  className,
}: {
  children: ReactNode
  /** A `/uputstvo#section` anchor, when the guide says more about this. */
  to?: string
  className?: string
}) {
  const { t } = useI18n()

  return (
    <div
      className={cn(
        'bg-muted/40 text-muted-foreground flex gap-2.5 rounded-lg border border-dashed px-3 py-2.5 text-sm leading-relaxed',
        className,
      )}
    >
      <Info className="text-muted-foreground/70 mt-0.5 size-4 shrink-0" aria-hidden />

      <div className="min-w-0">
        {children}

        {to && (
          <>
            {' '}
            <Link to={to} className="text-primary font-medium underline-offset-2 hover:underline">
              {t('help.readMore')}
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
